import React, { useEffect, useMemo, useState } from 'react';
import { Input, Message, SelectPicker } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanyDirectory } from '../../hooks/useQueries';

// Asked before the New Expense form opens: is this one expense, or the first of
// a batch? Choosing a group collects the name up front so the group card and
// the approval queue have something meaningful to show.
export default function ExpenseTypeModal({ open, onClose, onChooseIndividual, onChooseGroup, loading }) {
  const auth = useAuth();
  const canSubmitOnBehalf =
    typeof auth.hasPermission === 'function' && auth.hasPermission('submit_expenses_on_behalf');

  const [mode, setMode] = useState(null);
  const [groupName, setGroupName] = useState('');
  // A batch belongs to one employee, and every expense filed into it has to be
  // theirs — so unlike a single expense, where the owner is chosen on the form,
  // this has to be settled before the batch exists.
  const [employeeId, setEmployeeId] = useState(null);

  const directoryQuery = useCompanyDirectory({ enabled: canSubmitOnBehalf && open });

  useEffect(() => {
    if (!open) return;
    setMode(null);
    setGroupName('');
    setEmployeeId(auth.employee?.id || null);
  }, [open, auth.employee?.id]);

  const employeeOptions = useMemo(
    () => (directoryQuery.data || []).map((emp) => ({
      label: `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.email || 'Unknown',
      value: emp.id,
    })),
    [directoryQuery.data]
  );

  const isForSomeoneElse = Boolean(
    canSubmitOnBehalf
    && employeeId
    && auth.employee?.id
    && String(employeeId) !== String(auth.employee.id)
  );
  const selectedEmployeeName = employeeOptions.find((option) => option.value === employeeId)?.label || '';

  const canStartGroup = groupName.trim().length > 0 && Boolean(employeeId);

  const startGroup = () => {
    if (!canStartGroup || loading) return;
    onChooseGroup?.(groupName.trim(), employeeId);
  };

  return (
    <Modal open={open} onClose={loading ? undefined : onClose} size="xs">
      <Modal.Header>
        <Modal.Title>New Expense</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {mode === 'group' ? (
          <>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Name this group</div>
            <Input
              value={groupName}
              onChange={setGroupName}
              placeholder="e.g. Seattle onsite - January"
              disabled={loading}
              onPressEnter={startGroup}
            />
            <div className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
              Save each expense one at a time, then submit the whole group for approval when
              you're done.
            </div>

            {canSubmitOnBehalf ? (
              <>
                <div style={{ fontWeight: 600, margin: '14px 0 6px' }}>Whose expenses are these?</div>
                <SelectPicker
                  data={employeeOptions}
                  value={employeeId}
                  onChange={setEmployeeId}
                  cleanable={false}
                  block
                  placeholder="Select employee"
                  loading={directoryQuery.isLoading}
                  disabled={loading}
                />
                {isForSomeoneElse ? (
                  <Message type="warning" showIcon style={{ marginTop: 8 }}>
                    This group and everything in it will be owned by
                    {selectedEmployeeName ? ` ${selectedEmployeeName}` : ' the selected employee'}.
                    You'll still see it in your own list as the person who submitted it.
                  </Message>
                ) : null}
              </>
            ) : null}
          </>
        ) : (
          <div className="d-flex flex-column gap-2">
            <Button variant="primary" block icon="receipt" onClick={onChooseIndividual}>
              Individual expense
            </Button>
            <Button variant="accent" block icon="collection" onClick={() => setMode('group')}>
              Part of a group of expenses
            </Button>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {mode === 'group' ? (
          <>
            <Button icon="arrow-left" onClick={() => setMode(null)} disabled={loading}>
              Back
            </Button>
            <Button
              variant="primary"
              onClick={startGroup}
              disabled={!canStartGroup || loading}
              loading={loading}
            >
              Start group
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>Cancel</Button>
        )}
      </Modal.Footer>
    </Modal>
  );
}
